import {DumpsterFire} from "../DumpsterFire";
import {TimelineLite, gsap, Linear} from "gsap";
import {CustomWiggle} from "gsap/dist/CustomWiggle";
import {ISymbol} from "../../core/reel/simplereel/IReelData";

export enum DumpsterFireSymbolStates {
    IDLE,
    NORMAL,
    LANDING,
    WIN,
    NO_WIN,
    DISAPPEAR,
    TRIGGER,
    GLOW,
    GLOW_2
}

export enum SYMBOL_NAME {
    M1 = "M1",
    M2 = "M2",
    M3 = "M3",
    M4 = "M4",
    L1 = "L1",
    L2 = "L2",
    L3 = "L3",
    L4 = "L4",
    L5 = "L5",
    SCATTER = "S",
    X_BOMB = "XB",
    WILD = "W",
    XI = "XI",
    X_WAYS = "XW",
    U_X_BOMB = "upgradedXbomb",
    U_SCATTER = "upgradedScatter",
    FS_SCATTER = "fsScatter"
}

export class DumpsterFireSymbol extends PIXI.Sprite {
    protected _currentSpine: PIXI.spine.Spine;
    protected _newSpine: PIXI.spine.Spine;

    private readonly _name: string;

    constructor(symbolName: string) {
        super();
        this.isRemoved = false;

        this._name = symbolName;
        this.addSpine();
    }

    private _isRemoved: boolean;

    public get isRemoved(): boolean {
        return this._isRemoved;
    }

    public set isRemoved(value: boolean) {
        this._isRemoved = value;
    }

    private _reelId: number;

    public get reelId(): number {
        return this._reelId;
    }

    public set reelId(value: number) {
        this._reelId = value;
    }

    private _reelPosition: number;

    public get reelPosition(): number {
        return this._reelPosition;
    }

    public set reelPosition(value: number) {
        this._reelPosition = value;
    }

    private _reelTargetPosition: number;

    public get reelTargetPosition(): number {
        return this._reelTargetPosition;
    }

    public set reelTargetPosition(value: number) {
        this._reelTargetPosition = value;
    }

    private _symbolName: SYMBOL_NAME;

    get symbolName(): SYMBOL_NAME {
        return this._symbolName;
    }

    set symbolName(symbolName: SYMBOL_NAME) {
        this._symbolName = symbolName;
    }

    public getStateAnimation(state: DumpsterFireSymbolStates, toSymbol?: string): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        // if (this.state == state) {
        //     return tl;
        // }
        switch (state) {
            case DumpsterFireSymbolStates.NORMAL:
                let normalAnim: string = "normal";
                if (this._name === SYMBOL_NAME.M2 || ((this._name === SYMBOL_NAME.X_WAYS || this._name === SYMBOL_NAME.XI) && toSymbol === SYMBOL_NAME.M2)) {
                    normalAnim = DumpsterFire.isUSJurisdiction ? "normal_US" : "normal";
                }
                tl.add(DumpsterFire.createTimelineForAnimation(this._currentSpine, 0, normalAnim, false), 0);
                break;
            case DumpsterFireSymbolStates.IDLE:
                tl.add(DumpsterFire.createTimelineForAnimation(this._currentSpine, 0, "idle", false), 0);
                break;
            case DumpsterFireSymbolStates.LANDING:
                let landingAnim: string = "landing";
                if (this._name === SYMBOL_NAME.M2 || ((this._name === SYMBOL_NAME.X_WAYS || this._name === SYMBOL_NAME.XI) && toSymbol === SYMBOL_NAME.M2)) {
                    landingAnim = DumpsterFire.isUSJurisdiction ? "landing_US" : "landing";
                }
                tl.add(DumpsterFire.createTimelineForAnimation(this._currentSpine, 0, landingAnim, false), 0);
                break;
            case DumpsterFireSymbolStates.TRIGGER:
                tl.add(DumpsterFire.createTimelineForAnimation(this._currentSpine, 0, "trigger", false), 0);
                break;
            case DumpsterFireSymbolStates.NO_WIN:
                let noWinAnim: string = "noWin";
                if (this._name === SYMBOL_NAME.M2 || ((this._name === SYMBOL_NAME.X_WAYS || this._name === SYMBOL_NAME.XI) && toSymbol === SYMBOL_NAME.M2)) {
                    noWinAnim = DumpsterFire.isUSJurisdiction ? "noWin_US" : "noWin";
                }
                tl.add(DumpsterFire.createTimelineForAnimation(this._currentSpine, 0, noWinAnim, false), 0);
                break;
            case DumpsterFireSymbolStates.WIN:
                let winFrameAnim: string = "winFrameStart";
                if (this._name === SYMBOL_NAME.M2 || ((this._name === SYMBOL_NAME.X_WAYS || this._name === SYMBOL_NAME.XI) && toSymbol === SYMBOL_NAME.M2)) {
                    winFrameAnim = DumpsterFire.isUSJurisdiction ? "winFrameStart_US" : "winFrameStart";
                }
                tl.add(DumpsterFire.createTimelineForAnimation(this._currentSpine, 0, winFrameAnim, false), 0);
                break;
            case DumpsterFireSymbolStates.DISAPPEAR:
                let winAnim: string = "win";
                if (this._name === SYMBOL_NAME.M2 || ((this._name === SYMBOL_NAME.X_WAYS || this._name === SYMBOL_NAME.XI) && toSymbol === SYMBOL_NAME.M2)) {
                    winAnim = DumpsterFire.isUSJurisdiction ? "win_US" : "win";
                }
                tl.add(DumpsterFire.createTimelineForAnimation(this._currentSpine, 0, winAnim, false), 0);
                break;
            case DumpsterFireSymbolStates.GLOW:
                tl.add(() => {
                    this._currentSpine.autoUpdate = true;
                    this._currentSpine.state.setAnimation(0, "glow", true)
                }, 0)
                break;
            case DumpsterFireSymbolStates.GLOW_2:
                tl.add(() => {
                    this._currentSpine.state.setAnimation(0, "glow", false)
                }, 0)
                break;
            default:
                let defaultAnim: string = "normal";
                if (this._name === SYMBOL_NAME.M2 || ((this._name === SYMBOL_NAME.X_WAYS || this._name === SYMBOL_NAME.XI) && toSymbol === SYMBOL_NAME.M2)) {
                    defaultAnim = DumpsterFire.isUSJurisdiction ? "normal_US" : "normal";
                }
                tl.add(DumpsterFire.createTimelineForAnimation(this._currentSpine, 0, defaultAnim, false), 0);
        }
        return tl;
    }

    public init(initState: DumpsterFireSymbolStates): DumpsterFireSymbol {
        this.getStateAnimation(initState);
        return this;
    }

    public create(symbolName: SYMBOL_NAME, scaleValue: number): void {
        this._symbolName = symbolName;
        this._newSpine = new PIXI.spine.Spine(DumpsterFire.assetLoader.getSpineAsset(symbolName));
        let animName: string = `normal`;
        if (symbolName === SYMBOL_NAME.M2) {
            animName = !DumpsterFire.isUSJurisdiction ? `normal` : `normal_US`;
        }
        this._newSpine.state.setAnimation(0, animName, false);
        this._newSpine.pivot.set(0.5);
        this._newSpine.scale.x = scaleValue;
        this._newSpine.alpha = 0;
        this._newSpine.position.set(0);
        this.addChild(this._newSpine);
    }

    public changeSymbol(toSymbol: SYMBOL_NAME): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.add(() => {
            this.removeChildren();
        })
        tl.add(() => {
            this.create(toSymbol, 1);
            this._currentSpine = this._newSpine;
            this._currentSpine.alpha = 1;
        }, ">");
        return tl;
    }

    public changeXISymbol(toSymbol: SYMBOL_NAME): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.fromTo(this._currentSpine.scale, {x: 1}, {x: 0}, 0);
        tl.add(() => {
            this.removeChildren();
        })
        tl.add(() => {
            this.create(toSymbol, 0);
            this._currentSpine = this._newSpine;
            tl.fromTo(this._currentSpine.scale, {x: 0}, {x: 1});
            this._currentSpine.alpha = 1;
        }, ">");
        return tl;
    }

    public scaleSymbol(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.fromTo(this._currentSpine.scale, {x: 1, y: 1}, {x: 1.5, y: 1.5});
        return tl;
    }


    private addSpine(): void {
        this._currentSpine = new PIXI.spine.Spine(DumpsterFire.assetLoader.getSpineAsset(this._name));
        this.addChild(this._currentSpine);
    }
}